import { useState, useEffect, useMemo, type FormEvent } from 'react'
import { httpsCallable } from 'firebase/functions'
import { functions } from '../../lib/functions'
import { logGaEvent } from '../../lib/analytics'
import {
  SmsConsentCheckbox,
  SMS_CONSENT_TEXT,
  SMS_CONSENT_VERSION,
} from '../forms/SmsConsentCheckbox'

interface MasterclassTicketModalProps {
  isOpen: boolean
  onClose: () => void
  eventId: string
  eventName: string
  eventStartDate: Date
  ticketType: 'rider' | 'auditor'
  day?: string
  durationDays?: 2 | 3
  /**
   * Rider spots still open on this event. Used only to cap the "who else is
   * riding" dropdown so nobody is offered a party size the arena cannot hold.
   * The server re-checks this; leaving it undefined is safe.
   */
  riderSeatsRemaining?: number
}

interface RiderCompanion {
  relationship: string
  name: string
  email: string
}

interface CheckoutResult {
  url: string
}

interface MasterclassCheckoutPayload {
  ticketType: 'rider' | 'auditor'
  eventId: string
  day?: string
  paymentPlan: boolean
  firstName: string
  lastName?: string
  email: string
  phone?: string
  smsConsent?: boolean
  smsConsentText?: string
  smsConsentVersion?: string
  /**
   * Auditor group booking. One entry per human attending, purchaser first.
   * The backend turns length into the Stripe line_item quantity and the
   * webhook writes one roster row per name. Riders are always one seat.
   */
  attendeeNames?: string[]
  /**
   * Rider group booking. Everyone here is RIDING, so each one takes a horse
   * spot, is charged the full rider price, and gets their own release at their
   * own email address.
   */
  companions?: RiderCompanion[]
}

type Status = 'idle' | 'loading' | 'error'

const PAYMENT_PLAN_CUTOFF_DAYS = 105
const AUDITOR_PRICE_EACH = 20
const RIDER_PRICE_BY_DURATION: Record<2 | 3, number> = { 2: 599, 3: 849 }
// Must stay <= MAX_AUDITOR_GUESTS in website/src/createMasterclassCheckout.ts.
// The server slices anything longer, so a mismatch here would silently drop
// people the buyer thought they paid for.
const MAX_AUDITOR_PARTY = 4
// Must stay <= MAX_RIDER_PARTY in website/src/createMasterclassCheckout.ts.
const MAX_RIDER_PARTY = 4

const RELATIONSHIP_OPTIONS = [
  { value: 'spouse', label: 'Spouse or partner' },
  { value: 'child', label: 'Child' },
  { value: 'family', label: 'Other family member' },
  { value: 'friend', label: 'Friend' },
  { value: 'other', label: 'Someone else' },
]

const EMPTY_COMPANION: RiderCompanion = { relationship: 'spouse', name: '', email: '' }

const submitCheckout = httpsCallable<MasterclassCheckoutPayload, CheckoutResult>(
  functions,
  'createMasterclassCheckout',
)

export function MasterclassTicketModal({
  isOpen,
  onClose,
  eventId,
  eventName,
  eventStartDate,
  ticketType,
  day,
  durationDays,
  riderSeatsRemaining,
}: MasterclassTicketModalProps) {
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [smsConsent, setSmsConsent] = useState(false)
  const [paymentPlan, setPaymentPlan] = useState(false)
  const [partySize, setPartySize] = useState(1)
  // Fixed-length slot array so shrinking then growing the party does not
  // resurrect a name the buyer already removed from view.
  const [guestNames, setGuestNames] = useState<string[]>(
    () => Array(MAX_AUDITOR_PARTY - 1).fill(''),
  )
  // Riding companions. Fixed-length for the same reason as guestNames: shrinking
  // then growing the party must not resurrect someone the buyer removed.
  const [riderParty, setRiderParty] = useState(1)
  const [companions, setCompanions] = useState<RiderCompanion[]>(
    () => Array.from({ length: MAX_RIDER_PARTY - 1 }, () => ({ ...EMPTY_COMPANION })),
  )
  const [status, setStatus] = useState<Status>('idle')
  const [errorMessage, setErrorMessage] = useState('')

  const effectiveDuration: 2 | 3 = durationDays ?? 3
  const showPaymentPlanOption = effectiveDuration !== 2

  const daysUntilEvent = useMemo(() => {
    const now = new Date()
    const ms = eventStartDate.getTime() - now.getTime()
    return Math.floor(ms / (1000 * 60 * 60 * 24))
  }, [eventStartDate])

  const planAvailable =
    ticketType === 'rider' &&
    showPaymentPlanOption &&
    daysUntilEvent >= PAYMENT_PLAN_CUTOFF_DAYS

  const isAuditor = ticketType === 'auditor'
  // Attendee 1 is always the buyer. We reuse the name they already typed
  // instead of asking for it twice, which is where group forms usually
  // collect typos and mismatched roster rows.
  const purchaserName = `${firstName.trim()} ${lastName.trim()}`.trim()
  const activeGuestNames = isAuditor
    ? guestNames.slice(0, Math.max(0, partySize - 1))
    : []
  const guestNamesComplete = activeGuestNames.every((n) => n.trim().length > 0)
  const auditorTotal = AUDITOR_PRICE_EACH * (isAuditor ? partySize : 1)

  // ─── Riding companions ───────────────────────────────────────────────────
  const isRider = ticketType === 'rider'
  const riderPriceEach = RIDER_PRICE_BY_DURATION[effectiveDuration]
  // Never offer more spots than the arena has left. Undefined means the page
  // did not pass a count, in which case the server is the only guard.
  // Zero remaining still resolves to 1, not to the default 4. Treating 0 as
  // "no information" would offer a full group on a clinic that has no room at
  // all, and the buyer would only find out after filling the form in.
  const maxRiderParty =
    typeof riderSeatsRemaining === 'number'
      ? Math.max(1, Math.min(MAX_RIDER_PARTY, riderSeatsRemaining))
      : MAX_RIDER_PARTY
  // A payment plan is a recurring Stripe subscription, so it covers one rider
  // only. Choosing it collapses the party back to one rather than silently
  // charging for people it cannot cover.
  const companionsAllowed = isRider && !paymentPlan && maxRiderParty > 1
  const activeCompanions = isRider && !paymentPlan
    ? companions.slice(0, Math.max(0, Math.min(riderParty, maxRiderParty) - 1))
    : []
  const companionsComplete = activeCompanions.every(
    (c) => c.name.trim().length > 0 && c.email.trim().includes('@'),
  )
  const riderSeatCount = activeCompanions.length + 1
  const riderTotal = riderPriceEach * riderSeatCount

  function setCompanionField(index: number, field: keyof RiderCompanion, value: string) {
    setCompanions((prev) => {
      const next = [...prev]
      next[index] = { ...next[index], [field]: value }
      return next
    })
  }

  function setGuestName(index: number, value: string) {
    setGuestNames((prev) => {
      const next = [...prev]
      next[index] = value
      return next
    })
  }

  useEffect(() => {
    if (isOpen) {
      setFirstName('')
      setLastName('')
      setEmail('')
      setPhone('')
      setSmsConsent(false)
      setPaymentPlan(false)
      setPartySize(1)
      setGuestNames(Array(MAX_AUDITOR_PARTY - 1).fill(''))
      setRiderParty(1)
      setCompanions(
        Array.from({ length: MAX_RIDER_PARTY - 1 }, () => ({ ...EMPTY_COMPANION })),
      )
      setStatus('idle')
      setErrorMessage('')
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  useEffect(() => {
    if (!isOpen) return
    const original = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = original
    }
  }, [isOpen])

  function handleBackdropClick(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target === e.currentTarget) onClose()
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setStatus('loading')
    setErrorMessage('')
    try {
      // Defensive: 2-day masterclasses never offer a payment plan, even if
      // a stale checkbox state from a prior 3-day modal opening lingers.
      const effectivePaymentPlan =
        ticketType === 'rider' && effectiveDuration !== 2 ? paymentPlan : false
      // Step 3 of 4: handing off to Stripe. Fired before the await so a slow
      // or failed callable still records the intent. The payment plan carries
      // the same total as paying in full, so `value` does not change with it.
      const gaValue = isAuditor ? auditorTotal : riderTotal
      logGaEvent('begin_checkout', {
        currency: 'USD',
        value: gaValue,
        items: [{
          // Same slug the purchase event reports (PRICE_TO_SLUG on the
          // server); the specific event rides in item_category2.
          item_id: !isAuditor && effectiveDuration === 2
            ? 'riders-foundation-masterclass-2day'
            : 'riders-foundation-masterclass',
          item_name: eventName,
          item_category: ticketType,
          item_category2: eventId,
          item_variant: isAuditor ? (day ?? 'day-pass') : `${effectiveDuration}-day`,
          price: isAuditor ? AUDITOR_PRICE_EACH : riderPriceEach,
          quantity: isAuditor ? partySize : riderSeatCount,
        }],
      })

      const result = await submitCheckout({
        ticketType,
        eventId,
        day,
        paymentPlan: effectivePaymentPlan,
        firstName: firstName.trim(),
        lastName: lastName.trim() || undefined,
        email: email.trim(),
        phone: phone.trim() || undefined,
        // Consent is only ever recorded when the rider actually gave us a
        // number AND ticked the box themselves. Never inferred.
        smsConsent: smsConsent && phone.trim().length > 0,
        smsConsentText: smsConsent && phone.trim().length > 0 ? SMS_CONSENT_TEXT : undefined,
        smsConsentVersion: smsConsent && phone.trim().length > 0 ? SMS_CONSENT_VERSION : undefined,
        // Riders never send names: the server rejects a rider array longer
        // than one, and a one-element array would be pointless noise.
        attendeeNames: isAuditor
          ? [purchaserName, ...activeGuestNames.map((n) => n.trim())].filter(
              (n) => n.length > 0,
            )
          : undefined,
        // Riding companions. Omitted entirely when riding alone so the payload
        // is identical to what it was before this feature existed.
        companions:
          isRider && activeCompanions.length > 0
            ? activeCompanions.map((c) => ({
                relationship: c.relationship,
                name: c.name.trim(),
                email: c.email.trim(),
              }))
            : undefined,
      })
      const url = result.data.url
      if (url) {
        window.location.href = url
      } else {
        setStatus('error')
        setErrorMessage('Could not create checkout session. Please try again or email allie@advantagehorsemanship.com.')
      }
    } catch (err) {
      const fbErr = err as { code?: string; message?: string }
      const code = fbErr?.code ?? ''
      const message = fbErr?.message ?? ''
      let friendly = 'Something went wrong. Please try again or email allie@advantagehorsemanship.com.'
      if (code === 'functions/unavailable') {
        friendly = ticketType === 'auditor'
          ? 'Auditor day passes for this event are sold out.'
          : 'Rider tickets for this event are sold out.'
      } else if (code === 'functions/failed-precondition' && message.includes('payment plan unavailable')) {
        friendly = `Payment plan available ${PAYMENT_PLAN_CUTOFF_DAYS}+ days before event. Please select pay in full.`
      } else if (
        code === 'functions/resource-exhausted' ||
        code === 'functions/failed-precondition'
      ) {
        // Capacity and group-registration problems carry a message written for
        // the buyer ("only 2 rider spots left"). Replacing it with generic text
        // would hide the one thing they need to know to finish the purchase.
        friendly = message || friendly
      } else if (code === 'functions/invalid-argument') {
        friendly = 'Please check your name and email and try again.'
      } else if (code === 'functions/not-found') {
        friendly = 'This event is no longer available.'
      }
      setStatus('error')
      setErrorMessage(friendly)
    }
  }

  if (!isOpen) return null

  const isDisabled =
    status === 'loading' ||
    !firstName.trim() ||
    !email.trim() ||
    (isAuditor && !guestNamesComplete) ||
    (isRider && !companionsComplete)
  const riderPayInFullLabel =
    riderSeatCount > 1
      ? `$${riderTotal.toLocaleString()} today`
      : effectiveDuration === 2
        ? '$599 today'
        : '$849 today'
  const ridersPriceLabel =
    showPaymentPlanOption && paymentPlan
      ? '3 monthly payments of $283'
      : riderPayInFullLabel
  const submitLabel = status === 'loading'
    ? 'Loading...'
    : isAuditor
      ? `Continue to checkout: $${auditorTotal}`
      : `Continue to checkout: ${ridersPriceLabel}`

  return (
    <div
      className="fixed inset-0 z-50 flex items-start sm:items-center justify-center overflow-y-auto bg-black/70 px-4 py-8"
      onClick={handleBackdropClick}
    >
      <div className="relative my-auto flex max-h-[calc(100dvh-4rem)] w-full max-w-md flex-col overflow-hidden rounded-lg bg-white shadow-2xl">
        <div className="relative flex-shrink-0 bg-navy text-white px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 text-white/70 hover:text-white text-2xl leading-none"
            aria-label="Close"
          >
            &times;
          </button>
          <h2 className="font-heading text-2xl">Reserve Your Ticket</h2>
          <p className="font-body text-sm text-white/80 mt-1">
            {eventName}
          </p>
          {ticketType === 'auditor' && day && (
            <p className="font-body text-sm text-gold mt-1">
              Auditor Day Pass &middot; {day}
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-6 space-y-4">
          <div>
            <label className="block font-body text-sm font-medium text-navy mb-1">
              First name <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              maxLength={100}
              className="w-full min-h-[52px] px-3 py-2 border border-navy/20 rounded text-base focus:outline-none focus:border-gold"
              style={{ fontSize: '16px' }}
            />
          </div>

          <div>
            <label className="block font-body text-sm font-medium text-navy mb-1">
              Last name <span className="text-navy/50">(optional)</span>
            </label>
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              maxLength={100}
              className="w-full min-h-[52px] px-3 py-2 border border-navy/20 rounded text-base focus:outline-none focus:border-gold"
              style={{ fontSize: '16px' }}
            />
          </div>

          <div>
            <label className="block font-body text-sm font-medium text-navy mb-1">
              Email <span className="text-red-600">*</span>
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              maxLength={254}
              className="w-full min-h-[52px] px-3 py-2 border border-navy/20 rounded text-base focus:outline-none focus:border-gold"
              style={{ fontSize: '16px' }}
            />
          </div>

          <div>
            <label
              htmlFor="mc-phone"
              className="block font-body text-sm font-medium text-navy mb-1"
            >
              Mobile phone <span className="text-navy/50">(optional)</span>
            </label>
            <input
              id="mc-phone"
              name="phone"
              type="tel"
              autoComplete="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              maxLength={50}
              placeholder="(540) 555-0123"
              className="w-full min-h-[52px] px-3 py-2 border border-navy/20 rounded text-base focus:outline-none focus:border-gold"
              style={{ fontSize: '16px' }}
            />
            <p className="font-body text-xs text-navy/60 mt-1">
              Add your mobile number if you would like clinic reminders and day-of
              logistics by text. You can register without it.
            </p>
          </div>

          {/* A2P 10DLC: separate, unchecked, and never required to submit. */}
          <div className="pt-3 border-t border-navy/10">
            <SmsConsentCheckbox
              id="mc-sms-consent"
              checked={smsConsent}
              onChange={setSmsConsent}
            />
          </div>

          {/*
            Riding companions. Everyone added here is RIDING: they take a horse
            spot, pay the full rider price, and get their own release at their
            own email. Two people used to have to buy separately, and in practice
            the second purchase was made under the first person's name and email,
            which left a paid spot with no one it could be issued a release for.
          */}
          {isRider && (
            <div className="space-y-3 pt-3 border-t border-navy/10">
              <div>
                <label
                  htmlFor="mc-rider-party"
                  className="block font-body text-sm font-medium text-navy mb-1"
                >
                  Is anyone else riding with you?
                </label>
                <select
                  id="mc-rider-party"
                  name="riderParty"
                  value={riderParty}
                  disabled={!companionsAllowed}
                  onChange={(e) => {
                    const next = Number(e.target.value)
                    setRiderParty(next)
                    // A payment plan is one subscription for one rider. Adding a
                    // second rider clears it rather than quietly registering a
                    // person the plan does not cover.
                    if (next > 1) setPaymentPlan(false)
                  }}
                  className="w-full min-h-[52px] px-3 py-2 border border-navy/20 rounded text-base bg-white focus:outline-none focus:border-gold disabled:bg-navy/5 disabled:cursor-not-allowed"
                  style={{ fontSize: '16px' }}
                >
                  {Array.from({ length: maxRiderParty }, (_, i) => i + 1).map((n) => (
                    <option key={n} value={n}>
                      {n === 1
                        ? 'Just me'
                        : `${n} riders (me + ${n - 1})`}
                    </option>
                  ))}
                </select>
                {paymentPlan ? (
                  <p className="font-body text-xs text-navy/60 mt-1">
                    The payment plan covers one rider. Choose pay in full to
                    register more than one person on this order.
                  </p>
                ) : maxRiderParty === 1 ? (
                  <p className="font-body text-xs text-navy/60 mt-1">
                    Only one rider spot is left for this clinic.
                  </p>
                ) : (
                  <p className="font-body text-xs text-navy/60 mt-1">
                    Everyone added here is riding and brings a horse, so each one
                    takes a spot at the full rider price. If they are only coming
                    to watch, they need an auditor day pass instead.
                  </p>
                )}
              </div>

              {activeCompanions.map((c, i) => (
                <div
                  key={i}
                  className="space-y-3 rounded border border-navy/15 bg-navy/[0.03] p-3"
                >
                  <p className="font-body text-sm font-semibold text-navy">
                    Rider {i + 2}
                  </p>
                  <div>
                    <label
                      htmlFor={`mc-companion-rel-${i}`}
                      className="block font-body text-sm font-medium text-navy mb-1"
                    >
                      Who are they to you? <span className="text-red-600">*</span>
                    </label>
                    <select
                      id={`mc-companion-rel-${i}`}
                      name={`companionRelationship${i + 2}`}
                      value={c.relationship}
                      onChange={(e) => setCompanionField(i, 'relationship', e.target.value)}
                      className="w-full min-h-[52px] px-3 py-2 border border-navy/20 rounded text-base bg-white focus:outline-none focus:border-gold"
                      style={{ fontSize: '16px' }}
                    >
                      {RELATIONSHIP_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label
                      htmlFor={`mc-companion-name-${i}`}
                      className="block font-body text-sm font-medium text-navy mb-1"
                    >
                      Their full name <span className="text-red-600">*</span>
                    </label>
                    <input
                      id={`mc-companion-name-${i}`}
                      name={`companionName${i + 2}`}
                      type="text"
                      value={c.name}
                      onChange={(e) => setCompanionField(i, 'name', e.target.value)}
                      required
                      maxLength={100}
                      placeholder="First and last name"
                      className="w-full min-h-[52px] px-3 py-2 border border-navy/20 rounded text-base focus:outline-none focus:border-gold"
                      style={{ fontSize: '16px' }}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor={`mc-companion-email-${i}`}
                      className="block font-body text-sm font-medium text-navy mb-1"
                    >
                      Their email <span className="text-red-600">*</span>
                    </label>
                    <input
                      id={`mc-companion-email-${i}`}
                      name={`companionEmail${i + 2}`}
                      type="email"
                      value={c.email}
                      onChange={(e) => setCompanionField(i, 'email', e.target.value)}
                      required
                      maxLength={254}
                      className="w-full min-h-[52px] px-3 py-2 border border-navy/20 rounded text-base focus:outline-none focus:border-gold"
                      style={{ fontSize: '16px' }}
                    />
                    <p className="font-body text-xs text-navy/60 mt-1">
                      Their release of liability goes to this address. For a
                      child, use the parent or guardian's email.
                    </p>
                  </div>
                </div>
              ))}

              {riderSeatCount > 1 && (
                <p className="font-body text-sm text-navy">
                  {riderSeatCount} rider spots &times; ${riderPriceEach.toLocaleString()} ={' '}
                  <strong>${riderTotal.toLocaleString()}</strong>
                </p>
              )}
            </div>
          )}

          {ticketType === 'rider' && showPaymentPlanOption && (
            <div className="space-y-2 pt-2 border-t border-navy/10">
              <p className="font-body text-sm font-medium text-navy">Payment option</p>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="paymentOption"
                  checked={!paymentPlan}
                  onChange={() => setPaymentPlan(false)}
                  className="mt-1"
                />
                <span className="font-body text-sm text-navy">
                  Pay in full: <strong>{riderPayInFullLabel}</strong>
                </span>
              </label>
              <label className={`flex items-start gap-3 ${planAvailable && riderSeatCount === 1 ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}>
                <input
                  type="radio"
                  name="paymentOption"
                  checked={paymentPlan}
                  onChange={() => {
                    setPaymentPlan(true)
                    setRiderParty(1)
                  }}
                  disabled={!planAvailable || riderSeatCount > 1}
                  className="mt-1"
                />
                <span className="font-body text-sm text-navy">
                  Payment plan: <strong>$283/month for 3 months</strong> ($849 total)
                </span>
              </label>
              {!planAvailable && (
                <p className="font-body text-xs text-navy/60 pl-7">
                  Payment plan available {PAYMENT_PLAN_CUTOFF_DAYS}+ days before event. Please select pay in full.
                </p>
              )}
              {planAvailable && riderSeatCount > 1 && (
                <p className="font-body text-xs text-navy/60 pl-7">
                  The payment plan covers one rider. Register the group with pay
                  in full, or register each rider separately to use the plan.
                </p>
              )}
            </div>
          )}

          {ticketType === 'rider' && !showPaymentPlanOption && (
            <div className="pt-2 border-t border-navy/10">
              <p className="font-body text-sm text-navy">
                Pay in full: <strong>{riderPayInFullLabel}</strong>
              </p>
            </div>
          )}

          {isAuditor && (
            <div className="space-y-3 pt-3 border-t border-navy/10">
              <div>
                <label
                  htmlFor="mc-party-size"
                  className="block font-body text-sm font-medium text-navy mb-1"
                >
                  How many people are attending?{' '}
                  <span className="text-red-600">*</span>
                </label>
                <select
                  id="mc-party-size"
                  name="partySize"
                  value={partySize}
                  onChange={(e) => setPartySize(Number(e.target.value))}
                  className="w-full min-h-[52px] px-3 py-2 border border-navy/20 rounded text-base bg-white focus:outline-none focus:border-gold"
                  style={{ fontSize: '16px' }}
                >
                  {Array.from({ length: MAX_AUDITOR_PARTY }, (_, i) => i + 1).map(
                    (n) => (
                      <option key={n} value={n}>
                        {n === 1 ? 'Just me' : `${n} people (me + ${n - 1})`}
                      </option>
                    ),
                  )}
                </select>
                <p className="font-body text-xs text-navy/60 mt-1">
                  Every person watching from the rail needs their own day pass.
                  Groups larger than {MAX_AUDITOR_PARTY} need a second checkout,
                  or email allie@advantagehorsemanship.com.
                </p>
              </div>

              {activeGuestNames.map((value, i) => (
                <div key={i}>
                  <label
                    htmlFor={`mc-guest-${i}`}
                    className="block font-body text-sm font-medium text-navy mb-1"
                  >
                    Person {i + 2} full name{' '}
                    <span className="text-red-600">*</span>
                  </label>
                  <input
                    id={`mc-guest-${i}`}
                    name={`guestName${i + 2}`}
                    type="text"
                    value={value}
                    onChange={(e) => setGuestName(i, e.target.value)}
                    required
                    maxLength={100}
                    placeholder="First and last name"
                    className="w-full min-h-[52px] px-3 py-2 border border-navy/20 rounded text-base focus:outline-none focus:border-gold"
                    style={{ fontSize: '16px' }}
                  />
                </div>
              ))}

              {partySize > 1 && (
                <p className="font-body text-xs text-navy/60">
                  We check in {purchaserName || 'you'} plus{' '}
                  {partySize - 1 === 1 ? '1 guest' : `${partySize - 1} guests`} on
                  the roster for {day || 'the selected day'}.
                </p>
              )}

              <p className="font-body text-sm text-navy">
                Auditor day pass: <strong>${AUDITOR_PRICE_EACH} each</strong>
                {partySize > 1 && (
                  <>
                    {' '}
                    &times; {partySize} = <strong>${auditorTotal}</strong>
                  </>
                )}
              </p>
            </div>
          )}

          {status === 'error' && errorMessage && (
            <div className="bg-red-50 border border-red-200 rounded p-3">
              <p className="font-body text-sm text-red-800">{errorMessage}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={isDisabled}
            className="w-full bg-gold text-navy font-body font-bold tracking-wider uppercase py-3 min-h-[52px] rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gold/90 transition"
          >
            {submitLabel}
          </button>

          <p className="font-body text-xs text-navy/50 text-center">
            Powered by Stripe &middot; Secure checkout
          </p>
        </form>
      </div>
    </div>
  )
}
